import { UzStatusBadge } from '@utazzo/ui';
import React, { useMemo } from 'react';

import { listBlogCategories, type BlogCategoryListQuery } from '../blog-categories.api';
import { BlogCategoryFormDrawer } from '../components/BlogCategoryFormDrawer';
import {
  useBlogCategoriesList,
  useBulkDeleteBlogCategories,
  useBulkSetBlogCategoriesStatus,
  useDeleteBlogCategory,
} from '../use-blog-categories';

import type { WebsiteBlogCategoryListItem } from '@utazzo/shared';

import { type DataGridColumn } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCan } from '@/features/website-cms/lib';

const MIN_SEARCH_LENGTH = 2;

export const BlogCategoriesListPage = (): React.JSX.Element => {
  const canWrite = useCan('blogs.write');
  const deleteCategory = useDeleteBlogCategory();
  const bulkDeleteMut = useBulkDeleteBlogCategories();
  const bulkSetStatusMut = useBulkSetBlogCategoriesStatus();

  const columns: ListScreenColumns<WebsiteBlogCategoryListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<WebsiteBlogCategoryListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        cell: (row) => <span className="font-medium text-zinc-900">{row.name}</span>,
      },
      {
        id: 'slug',
        header: 'Identifier',
        cell: (row) => (
          <code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-600">
            {row.slug}
          </code>
        ),
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        width: '80px',
        cell: (row) => <span className="text-zinc-500">{row.sortOrder}</span>,
      },
      {
        id: 'postCount',
        header: 'Posts',
        width: '80px',
        cell: (row) => <span className="text-zinc-500">{row.postCount}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  const toQuery = useMemo(
    () =>
      (
        state: { filters: Record<string, string | undefined> },
        base: Partial<BlogCategoryListQuery>,
      ): BlogCategoryListQuery => {
        const status = state.filters.status;
        return {
          ...(base as BlogCategoryListQuery),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      },
    [],
  );

  return (
    <ListScreen<WebsiteBlogCategoryListItem, BlogCategoryListQuery>
      title="Blog Interests (Categories)"
      description="Master list of story categories. Powers the 'Browse by Interest' story filters and the blog editor's category picker. Inactive categories are hidden from both."
      useList={useBlogCategoriesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={toQuery}
      columns={columns}
      filters={[
        {
          id: 'status',
          label: 'Status',
          options: [
            { value: '', label: 'Active + inactive' },
            { value: 'active', label: 'Active only' },
            { value: 'inactive', label: 'Inactive only' },
          ],
          widthClass: 'w-48',
        },
      ]}
      getRowId={(row) => row.id}
      bulkActions={
        canWrite
          ? {
              delete: {
                onDelete: async (rows) => {
                  await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
                },
                confirm: (rows) => ({
                  title: `Delete ${rows.length} categor${rows.length === 1 ? 'y' : 'ies'}?`,
                  description: 'Categories still used by live posts will be skipped and reported.',
                }),
              },
              setActive: {
                onSetActive: async (rows, isActive) =>
                  bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
                confirm: (rows, isActive) => ({
                  title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} categor${rows.length === 1 ? 'y' : 'ies'}?`,
                  description: isActive
                    ? 'They will reappear in the story filters and editor picker.'
                    : 'They will be hidden from new story filters but kept on historical posts.',
                }),
              },
            }
          : undefined
      }
      export={{
        fetchPage: (q) => listBlogCategories(q),
        filename: 'blog-categories',
        csv: [
          { header: 'Name', value: (r) => r.name },
          { header: 'Slug', value: (r) => r.slug },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Posts', value: (r) => r.postCount },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
        ],
      }}
      createButton={canWrite ? { label: 'Add category' } : undefined}
      renderDrawer={({ open, mode, row, close }) => (
        <BlogCategoryFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          category={row}
        />
      )}
      rowActions={{
        edit: canWrite,
        delete: canWrite
          ? {
              onDelete: (row) => deleteCategory.mutateAsync(row.id),
              confirm: (row) => ({
                title: 'Delete this category?',
                description:
                  row.postCount > 0
                    ? `${row.name} is used by ${row.postCount} post(s). Reassign those posts before deleting.`
                    : `${row.name} will be soft-deleted and removed from the story filters.`,
              }),
            }
          : undefined,
      }}
      copy={{
        searchPlaceholder: 'Search by name or slug (min 2 characters)',
        emptyTitle: 'No categories match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load categories',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
